// Signature WAF event side drawer — shared shell + v3 drawer UI tokens.

import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Stack, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ShieldPlus } from "lucide-react";
import copy from "copy-to-clipboard";

import axios from "../../../../utils/axios/v1/userAxios";
import useSite from "../../../../hooks/user/useSite";
import useGlobalSite from "../../../../hooks/user/useGlobalSite";
import usePermissions from "../../../../hooks/usePermissions";
import colors from "../../../../utils/colors";
import { buildSignatureWafDrawerView, signatureViewToFilterEvent } from "./buildSignatureWafDrawerView";
import {
  buildSimilarWafEventNavigationState,
  WAF_EVENTS_PATH,
} from "./wafEventNavigation";
import {
  buildFirewallBlockIpState,
  FIREWALL_RULES_PATH,
} from "../services/firewall/firewallNavigation";
import WafEventDrawerShell from "./WafEventDrawerShell";
import SmartParseDrawerHeader from "./v3/SmartParseDrawerHeader";
import SignatureWafDrawerHero from "./v3/SignatureWafDrawerHero";
import SignatureWafEventDrawerBody from "./v3/SignatureWafEventDrawerBody";
import LayerdIcon from "../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../icons/detectionLucideIcons";
import {
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  secondaryButtonStyle,
  primaryButtonStyle,
  formatDrawerFooterLabel,
} from "./wafEventDrawerShared";
import { spLh } from "./v3/smartParseDrawerUi";

function DrawerFooterContext({ site, host }) {
  const siteLabel = site?.name || site?.site_name || site?.site_id;
  const footerLabel = formatDrawerFooterLabel(siteLabel, host);
  if (!footerLabel) return null;

  const isActive = site?.status === "Active" || site?.enabled === true;

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      {isActive && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: colors.green[200],
            flexShrink: 0,
          }}
        />
      )}
      <Typography
        sx={{
          fontSize: 13,
          lineHeight: spLh.px21,
          color: TEXT_SECONDARY,
          wordBreak: "break-all",
        }}
      >
        {footerLabel}
      </Typography>
    </Stack>
  );
}

function listRowPath(event) {
  if (!event) return null;
  const uri = event.uri || event.target?.uri || event.target?.path;
  if (!uri) return null;
  const q = uri.indexOf("?");
  return q === -1 ? uri : uri.slice(0, q);
}

export default function SignatureWafEventDrawer({ open, onClose, requestId, event: listEvent }) {
  const navigate = useNavigate();
  const { siteList } = useSite();
  const { effectiveSiteId: siteID } = useGlobalSite();
  const { getPermissionsForSite, PERMISSIONS } = usePermissions();
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);

  useEffect(() => {
    if (!open || !requestId) {
      setRaw(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    setRaw(null);

    axios
      .get(`log/waf_event/${requestId}`, {
        params: { waf_event: "signature_waf" },
        signal: ctrl.signal,
      })
      .then((res) => setRaw(res.data || null))
      .catch((err) => {
        if (err?.message === "canceled" || err?.code === "ERR_CANCELED") return;
        setError(err?.message || "Failed to load event");
      })
      .finally(() => setLoading(false));

    return () => ctrl.abort();
  }, [open, requestId]);

  const view = useMemo(() => buildSignatureWafDrawerView(raw), [raw]);
  const displayError = error || (!loading && !view && requestId ? "No event data." : null);

  const selectedSite = useMemo(
    () => siteList?.find((s) => s.site_id === siteID) ?? null,
    [siteList, siteID],
  );

  const canWriteFirewall = useMemo(() => {
    if (!selectedSite) return false;
    return getPermissionsForSite(selectedSite).hasPermission(PERMISSIONS.FIREWALL_WRITE);
  }, [selectedSite, getPermissionsForSite, PERMISSIONS]);

  const sourceIp = view?.requestDetails?.sourceIp || listEvent?.ip;
  const host = view?.requestDetails?.host || listEvent?.host;

  const handleCopy = (value) => {
    if (!value || value === "—") return;
    copy(String(value));
    setSnackOpen(true);
    setTimeout(() => setSnackOpen(false), 2000);
  };

  const handleFilterSimilar = (filterType) => {
    const navState = buildSimilarWafEventNavigationState(
      signatureViewToFilterEvent(view),
      filterType,
    );
    if (!navState) return;
    onClose?.();
    navigate(WAF_EVENTS_PATH, { state: navState });
  };

  const handleAddToBlockList = () => {
    const siteLabel = selectedSite?.name || selectedSite?.site_name || selectedSite?.site_id;
    const navState = buildFirewallBlockIpState(sourceIp, siteLabel);
    if (!navState) return;
    onClose?.();
    navigate(FIREWALL_RULES_PATH, { state: navState });
  };

  if (!open || !requestId) return null;

  const method = view?.requestDetails?.method || listEvent?.method;
  const path = view?.requestDetails?.path || listRowPath(listEvent);

  const footerActions = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Button
        onClick={onClose}
        startIcon={<LayerdIcon icon={DETECTION_ICONS.close} size={16} color="neutral" />}
        sx={secondaryButtonStyle}
      >
        Close
      </Button>
      {canWriteFirewall && sourceIp && (
        <Button
          onClick={handleAddToBlockList}
          startIcon={<ShieldPlus size={16} />}
          sx={{
            ...primaryButtonStyle,
            boxShadow: "0 4px 14px rgba(99,113,247,0.30)",
          }}
        >
          Add to block list
        </Button>
      )}
    </Stack>
  );

  return (
    <>
      <WafEventDrawerShell
        open={open}
        onClose={onClose}
        title="Signature Event"
        loading={loading}
        error={displayError}
        headerVariant="hero"
        headerContent={(
          <SmartParseDrawerHeader
            embedded
            title="Signature Event"
            eventId={requestId}
            method={method}
            path={path}
            onCopyEventId={handleCopy}
          />
        )}
        heroContent={view ? <SignatureWafDrawerHero view={view} /> : null}
        footerLeft={<DrawerFooterContext site={selectedSite} host={host} />}
        footerActions={footerActions}
      >
        {view && (
          <SignatureWafEventDrawerBody
            view={view}
            onCopy={handleCopy}
            onFilterSimilar={handleFilterSimilar}
          />
        )}
      </WafEventDrawerShell>

      {snackOpen && (
        <Box
          sx={{
            position: "fixed",
            bottom: 80,
            right: 24,
            backgroundColor: TEXT_PRIMARY,
            color: "#fff",
            px: 2,
            py: 1,
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 1400,
          }}
        >
          <Typography sx={{ fontSize: 14, color: "#fff" }}>
            Copied to clipboard!
          </Typography>
        </Box>
      )}
    </>
  );
}

SignatureWafEventDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  requestId: PropTypes.string,
  event: PropTypes.object,
};
